import React, { useState } from "react";
import {
  Form,
  Select,
  Input,
  Button,
  Space,
  Tag,
  InputNumber,
  message,
} from "antd";
import { entityFieldMappings } from "./entityFieldMappings";

/**
 * @typedef {Object} FieldConfigurationFormProps
 * @property {(values: Object) => void} onFinish - Callback function called when form is submitted
 * @property {Object} intl - Internationalization object for translations
 */

/**
 * A form component for configuring PDF field mappings with various properties.
 * Supports both simple fields and marker fields with multiple values.
 *
 * @component
 * @param {FieldConfigurationFormProps} props
 *
 * @example
 * ```jsx
 * <FieldConfigurationForm
 *   onFinish={(values) => console.log(values)}
 *   intl={intlObject}
 * />
 * ```
 */
const FieldConfigurationForm = ({ onFinish, intl }) => {
  const [form] = Form.useForm();
  /**
   * @type {[string, Function]} markerValue - Current value in marker input field
   */
  const [markerValue, setMarkerValue] = useState("");
  /**
   * @type {[Array<string>, Function]} markerValues - Collection of added marker values
   */
  const [markerValues, setMarkerValues] = useState([]);
  const [arraySize, setArraySize] = useState(0);
  const [currentArrayIndex, setCurrentArrayIndex] = useState(0);
  const [subFieldName, setSubFieldName] = useState("");

  const entity = Form.useWatch("entity", form);
  const type = Form.useWatch("type", form);
  const value_type = Form.useWatch("value_type", form);

  /**
   * Adds a new marker value to the collection if it's not already present
   */
  const addMarkerValue = () => {
    if (markerValue && !markerValues.includes(markerValue)) {
      setMarkerValues([...markerValues, markerValue]);
      setMarkerValue("");
    }
  };

  /**
   * Removes a marker value from the collection
   * @param {string} value - The marker value to remove
   */
  const removeMarkerValue = (value) => {
    setMarkerValues(markerValues.filter((v) => v !== value));
  };

  return (
    <Form
      form={form}
      onFinish={(values) => {
        const output = { ...values };

        // Handle marker type
        if (type === "checkbox") {
          if (!markerValues.length) {
            message.error(
              intl.formatMessage({
                id: "app.settings.forms.add_at_least_one_marker",
              })
            );
            return;
          }
          output.markerValues = markerValues; // Pass the markerValues directly
        }

        // Handle array type
        if (type === "list") {
          if (!arraySize || arraySize <= 0) {
            message.error(
              intl.formatMessage({
                id: "app.settings.forms.list_size_required",
              })
            );
            return;
          }
          if (!subFieldName) {
            message.error(
              intl.formatMessage({
                id: "app.settings.forms.subfield_name_required",
              })
            );
            return;
          }
          output.arraySize = arraySize;
          output.subFieldName = subFieldName;
          // Generate array of identical keys for mapping
          output.markerValues = Array.from(
            { length: arraySize },
            () => subFieldName
          );
        }

        // Handle const value_type - ensure value is included in output
        if (value_type === "const") {
          output.value = values.value;
          // Remove entity and name since they're not needed for const values
          delete output.entity;
          delete output.name;
        }

        onFinish(output);
      }}
      layout="vertical"
    >
      {value_type !== "const" && (
        <>
          <Form.Item
            name="entity"
            label={intl.formatMessage({
              id: "app.settings.forms.field.entity",
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "app.settings.forms.field.entityRequired",
                }),
              },
            ]}
          >
            <Select>
              <Select.Option value="patient">
                {intl.formatMessage({ id: "app.settings.forms.patientLabel" })}
              </Select.Option>
              <Select.Option value="employee">
                {intl.formatMessage({ id: "app.settings.forms.employeeLabel" })}
              </Select.Option>
              <Select.Option value="company">
                {intl.formatMessage({ id: "app.settings.forms.companyLabel" })}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label={intl.formatMessage({ id: "app.settings.forms.field.name" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "app.settings.forms.field.nameRequired",
                }),
              },
            ]}
          >
            <Select
              disabled={!entity}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              optionFilterProp="children"
            >
              {entity &&
                entityFieldMappings[entity]?.map((field, index) => (
                  <Select.Option
                    key={`${field.value}-${field.label}-${index}`}
                    value={field.value}
                  >
                    {field.label}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </>
      )}

      {type === "list" && (
        <Form.Item
          label={intl.formatMessage({
            id: "app.settings.forms.field.subFieldName",
          })}
          required
        >
          <Input
            value={subFieldName}
            onChange={(e) => setSubFieldName(e.target.value)}
            placeholder={intl.formatMessage({
              id: "app.settings.forms.field.subFieldNamePlaceholder",
            })}
          />
        </Form.Item>
      )}

      <Form.Item
        name="type"
        label={intl.formatMessage({ id: "app.settings.forms.field.type" })}
        initialValue="text"
      >
        <Select>
          <Select.Option value="text">
            {intl.formatMessage({ id: "app.settings.forms.field.typeSimple" })}
          </Select.Option>
          <Select.Option value="checkbox">
            {intl.formatMessage({ id: "app.settings.forms.field.typeMarker" })}
          </Select.Option>
          <Select.Option value="list">
            {intl.formatMessage({ id: "app.settings.forms.field.typeList" })}
          </Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="value_type"
        label={intl.formatMessage({ id: "app.settings.forms.field.valueType" })}
        initialValue="text"
      >
        <Select>
          <Select.Option value="text">
            {intl.formatMessage({
              id: "app.settings.forms.field.valueTypeText",
            })}
          </Select.Option>
          <Select.Option value="const">
            {intl.formatMessage({
              id: "app.settings.forms.field.valueTypeConst",
            })}
          </Select.Option>
        </Select>
      </Form.Item>

      {value_type === "const" && (
        <Form.Item
          name="value"
          label={intl.formatMessage({
            id: "app.settings.forms.field.constValue",
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: "app.settings.forms.field.constValueRequired",
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>
      )}

      <Form.Item
        name="format"
        label={intl.formatMessage({ id: "app.settings.forms.field.format" })}
      >
        <Select allowClear>
          <Select.Option value="phone">
            {intl.formatMessage({ id: "app.settings.forms.field.formatPhone" })}
          </Select.Option>
          <Select.Option value="DDMMYYYY">
            {intl.formatMessage({ id: "app.settings.forms.field.formatDate" })}
          </Select.Option>
          <Select.Option value="DD/MM/YYYY">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatDate_date",
            })}
          </Select.Option>
          <Select.Option value="MM/YYYY">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatDate_month_year",
            })}
          </Select.Option>
          <Select.Option value="DD">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatDate_day",
            })}
          </Select.Option>
          <Select.Option value="MM">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatDate_month",
            })}
          </Select.Option>
          <Select.Option value="YYYY">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatDate_year",
            })}
          </Select.Option>
          <Select.Option value="number">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatNumber",
            })}
          </Select.Option>
          <Select.Option value="currency">
            {intl.formatMessage({
              id: "app.settings.forms.field.formatCurrency",
            })}
          </Select.Option>
          <Select.Option value="logo">
            {intl.formatMessage({
              id: "app.settings.forms.field.logo",
            })}
          </Select.Option>
          <Select.Option value="localized">
            {intl.formatMessage({
              id: "app.settings.forms.field.localized",
            })}
          </Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="spacing"
        label={intl.formatMessage({ id: "app.settings.forms.field.spacing" })}
      >
        <Input type="number" min={0} />
      </Form.Item>

      {type === "list" && (
        <>
          <Form.Item
            label={intl.formatMessage({
              id: "app.settings.forms.field.listSize",
            })}
            required
          >
            <InputNumber
              min={1}
              value={arraySize}
              onChange={(value) => {
                setArraySize(value);
                setCurrentArrayIndex(0);
              }}
              placeholder={intl.formatMessage({
                id: "app.settings.forms.field.arraySizePlaceholder",
              })}
            />
          </Form.Item>
        </>
      )}

      {type === "checkbox" && (
        <div>
          <Form.Item
            label={intl.formatMessage({
              id: "app.settings.forms.field.addMarkerValues",
            })}
          >
            <Space>
              <Input
                value={markerValue}
                onChange={(e) => setMarkerValue(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "app.settings.forms.field.markerValuePlaceholder",
                })}
                onPressEnter={(e) => {
                  e.preventDefault();
                  addMarkerValue();
                }}
              />
              <Button onClick={addMarkerValue} type="primary">
                {intl.formatMessage({
                  id: "app.settings.forms.field.addValue",
                })}
              </Button>
            </Space>
          </Form.Item>
          {markerValues.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {markerValues.map((value) => (
                <Tag
                  key={value}
                  closable
                  onClose={() => removeMarkerValue(value)}
                  style={{ margin: "4px" }}
                >
                  {value}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}

      <Form.Item>
        <Button type="primary" htmlType="submit">
          {type === "checkbox"
            ? intl.formatMessage({
                id: "app.settings.forms.field.startPlacingMarkers",
              })
            : type === "list"
            ? intl.formatMessage({
                id: "app.settings.forms.field.startPlacingListMarkers",
              })
            : intl.formatMessage({ id: "app.settings.forms.field.addField" })}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default FieldConfigurationForm;
